import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Switch,
  Textarea,
  Typography,
} from '@mui/joy';
import type { Customer } from '../../services/customerService';
import type { Project } from '../../types/project';
import type { ProjectEvent } from '../../components/EventManager';
import { documentsService } from '../../modules/docs/services/documents.service';
import type { Document } from '../../modules/docs/types/documents.types';
import { documentDisplayRef } from '../../modules/docs/utils/invoiceLedger';
import { directSupabase } from '../../lib/supabaseClient';
import { syncLinkedEventDatesFromOutlook } from './jobCardOutlookSync';
import {
  checkCardDiaryEvent,
  deleteCardDiaryEvent,
  moveBookingToOverride,
  saveCardDiaryEvent,
} from './jobCardDiary';
import { useProfile } from '../../hooks/useProfile';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { FaEdit, FaTrash, FaWhatsapp } from 'react-icons/fa';
import { MdEventBusy } from 'react-icons/md';
import { jobCardShareText } from './jobCardShare';
import { IconWrapper } from '../../components/IconWrapper';
import { resolveDocumentCustomerIdentity } from './workspaceBillingIdentity';
import {
  addJobComment,
  currentJobCardClosure,
  currentJobCompletion,
  deleteJobCardImage,
  deleteJobComment,
  listAssignableCrew,
  listJobAssignees,
  listJobCardImages,
  listJobComments,
  listJobCompletionEvents,
  listJobCardOrderAcceptanceFiles,
  listJobSubcontractorIds,
  listSubcontractorAccounts,
  openJobCardOrderAcceptance,
  recordJobCompletion,
  recordJobEnded,
  recordJobRelease,
  setJobAssignees,
  setJobSubcontractors,
  updateJobCard,
  updateJobComment,
  uploadJobCardImage,
} from './jobCard.service';
import type {
  AssignableProfile,
  JobAssignee,
  JobCardImage,
  JobCardRecord,
  JobComment,
  JobCardOrderAcceptanceFile,
  JobCompletionEvent,
  SubcontractorAccount,
} from './jobCard.types';
import {
  JobCardAddPhotosButton,
  JobCardImageGallery,
  JobCardPendingThumbs,
  usePendingImages,
} from './JobCardImageAttach';
import JobCardOaPreviewModal from './JobCardOaPreviewModal';
import { useOpenedOaPreview } from './useOpenedOaPreview';
import {
  asProjectMetadata,
  persistedSurveyNotes,
  SURVEY_NOTES_PLACEHOLDER,
  surveyNotesFrom,
  withPerCardSurveyNotes,
} from './jobCardSurveyNotes';
import {
  formatInstallSpan,
  installDateOverrideFrom,
  installEndOverrideFrom,
  jobCardInstallSpan,
  toInstallDateOnly,
  toInstallTimeOnly,
  todayInstallDateOnly,
  withInstallTime,
  withPerCardInstallOverride,
} from './jobCardInstallDate';

interface JobCardProps {
  jobCard: JobCardRecord;
  project: Project;
  customer: Customer | null;
  allowAssign?: boolean;
  showSpecLinks?: boolean;
  onAttestedComplete?: () => Promise<void>;
  onReleased?: () => Promise<void>;
  onProjectPatch?: (patch: Partial<Project>) => Promise<void>;
  onJobCardPatch?: (next: JobCardRecord) => void;
  onDeleteCard?: () => Promise<void>;
  onAssigneesChanged?: () => void;
  isOldest?: boolean;
  /** Title of the card before this one, shown so its earlier comments are easy to find. */
  continuedFrom?: string | null;
  /** Cards raised after this one: where the job can carry on when this card is ended. */
  laterCards?: Array<{ id: string; title: string }>;
}

const END_REASONS = ['Delay', 'Crew change', 'Other'] as const;

function formatStamp(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/London',
  });
}

/** Job card times are booked in quarter hours. */
const QUARTER_HOUR_SECONDS = 15 * 60;

/** Rounds a typed "HH:MM" to the nearest quarter hour, capped at 23:45 so it stays on the same day. */
function toQuarterHour(value: string): string {
  const match = String(value || '').match(/^(\d{2}):(\d{2})/);
  if (!match) return value;
  const total = Math.min(Math.round((Number(match[1]) * 60 + Number(match[2])) / 15) * 15, 23 * 60 + 45);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function crewLabel(person: AssignableProfile | JobAssignee, subcontractor = false) {
  const id = 'profile_id' in person ? person.profile_id : person.id;
  const name = person.full_name || person.email || id;
  const role = person.role === 'admin' ? 'Admin'
    : person.role === 'fitter' ? 'Fitter'
    : person.role === 'labourer' ? 'Labourer'
    : person.role;
  const tags = [role, subcontractor ? 'Subcontractor' : null].filter(Boolean).join(' · ');
  return tags ? `${name} (${tags})` : String(name);
}

function parseEventDate(dateStr: string) {
  if (dateStr && !dateStr.match(/(Z|[+-][0-9]{2}:?[0-9]{2})$/)) {
    return new Date(`${dateStr}Z`);
  }
  return new Date(dateStr);
}

function formatEventStamp(value?: string | null) {
  if (!value) return null;
  const date = parseEventDate(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/London',
  });
}

function latestTypedEvent(events: ProjectEvent[], type: string) {
  return events
    .filter((event) => event.event_type === type && event.status !== 'cancelled')
    .sort((a, b) => {
      const aDated = a.event_date ? 1 : 0;
      const bDated = b.event_date ? 1 : 0;
      if (aDated !== bDated) return bDated - aDated;
      const aMs = Date.parse(a.event_date || a.created_at || '') || 0;
      const bMs = Date.parse(b.event_date || b.created_at || '') || 0;
      return bMs - aMs;
    })[0];
}

function asMetadata(project: Project): Record<string, unknown> {
  return asProjectMetadata(project);
}

function sensitiveCustomerFrom(project: Project): boolean {
  const value = asMetadata(project).sensitive_customer;
  return value === true || value === 'Y' || value === 'yes';
}

function JobCardSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Typography level="title-sm" sx={{ mb: hint ? 0.25 : 1 }}>{title}</Typography>
      {hint && (
        <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.25 }}>{hint}</Typography>
      )}
      {children}
    </Box>
  );
}

const JobCard: React.FC<JobCardProps> = ({
  jobCard,
  project,
  customer,
  allowAssign = false,
  showSpecLinks = false,
  onAttestedComplete,
  onReleased,
  onProjectPatch,
  onJobCardPatch,
  onDeleteCard,
  onAssigneesChanged,
  isOldest = false,
  continuedFrom = null,
  laterCards = [],
}) => {
  const { user } = useSupabaseAuth();
  const { profile, isFitter, isLabourer, isSiteCrew, isSupplier } = useProfile();
  const [events, setEvents] = useState<ProjectEvent[]>([]);
  const [docs, setDocs] = useState<Document[]>([]);
  const [assignees, setAssignees] = useState<JobAssignee[]>([]);
  const [shareCopied, setShareCopied] = useState(false);
  const [crew, setCrew] = useState<AssignableProfile[]>([]);
  const [oaFiles, setOaFiles] = useState<JobCardOrderAcceptanceFile[]>([]);
  const [comments, setComments] = useState<JobComment[]>([]);
  const [surveyImages, setSurveyImages] = useState<JobCardImage[]>([]);
  const [uploadingSurvey, setUploadingSurvey] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const commentPhotos = usePendingImages();
  const [completionEvents, setCompletionEvents] = useState<JobCompletionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingAssignees, setSavingAssignees] = useState(false);
  /** Directory (CIS) subcontractors; office only. Null until loaded, or when the table isn't there yet. */
  const [subAccounts, setSubAccounts] = useState<SubcontractorAccount[] | null>(null);
  const [subIds, setSubIds] = useState<string[]>([]);
  const [savingSubs, setSavingSubs] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobComment | null>(null);
  const { preview: oaPreview, openingId: openingFile, openFile: openOaFile, close: closeOaPreview } = useOpenedOaPreview();
  const [attestOpen, setAttestOpen] = useState(false);
  const [jobDone, setJobDone] = useState(false);
  const [csfIssued, setCsfIssued] = useState(false);
  const [attesting, setAttesting] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [endReason, setEndReason] = useState<string>(END_REASONS[0]);
  const [endContinuedOn, setEndContinuedOn] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const [surveyDraft, setSurveyDraft] = useState(() => surveyNotesFrom(jobCard, project, isOldest));
  const [savingSurvey, setSavingSurvey] = useState(false);
  const [savingInstallOverride, setSavingInstallOverride] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [movingToPending, setMovingToPending] = useState(false);
  /** This card's Outlook event (job card 1: the booking's) has been deleted in Outlook. */
  const [diaryMissing, setDiaryMissing] = useState(false);
  const [savingSensitive, setSavingSensitive] = useState(false);
  const [deletingCard, setDeletingCard] = useState(false);
  const [deleteCardOpen, setDeleteCardOpen] = useState(false);
  const siteIdentity = useMemo(
    () => resolveDocumentCustomerIdentity({ customer, project, kind: 'JobCard' }),
    [customer, project]
  );

  const loadInstallEvents = async (options: { syncOutlook?: boolean } = {}) => {
    const eventsRes = await directSupabase
      .from('project_events')
      .select('*')
      .eq('project_id', project.id)
      .in('event_type', ['installationDateConfirmation', 'deliveryDateConfirmation'])
      .order('event_date', { ascending: false });
    if (eventsRes.error) throw new Error(eventsRes.error.message);
    const rows = (eventsRes.data || []) as ProjectEvent[];
    setEvents(rows);
    if (options.syncOutlook) {
      void syncLinkedEventDatesFromOutlook(rows as any[], user?.id)
        .then(({ changed, missing }) => {
          if (isOldest) {
            const live = latestTypedEvent(rows, project.supply_only ? 'deliveryDateConfirmation' : 'installationDateConfirmation');
            setDiaryMissing(!!live?.calendar_event_id && missing.includes(live.calendar_event_id));
          }
          return changed ? loadInstallEvents() : undefined;
        })
        .catch((err) => console.warn('Failed to check Outlook for event changes', err));
      if (!isOldest) void syncCardDiaryFromOutlook();
    }
  };

  /** Later cards: copy date moves made in Outlook onto the card, and flag a deleted event. */
  const syncCardDiaryFromOutlook = async () => {
    if (!jobCard.calendar_event_id) {
      setDiaryMissing(false);
      return;
    }
    try {
      const result = await checkCardDiaryEvent({
        eventId: jobCard.calendar_event_id,
        saved: {
          start: installDateOverrideFrom(jobCard, project),
          endDay: toInstallDateOnly(jobCard.install_end_date_override),
          end: installEndOverrideFrom(jobCard, project),
        },
      });
      setDiaryMissing(result.missing);
      if (!result.override) return;
      const next = await updateJobCard(jobCard.id, {
        install_date_override: toInstallDateOnly(result.override.start),
        install_end_date_override: result.override.endDay,
      });
      if (onProjectPatch) {
        await onProjectPatch({
          metadata: withPerCardInstallOverride(project, jobCard.id, result.override.start, result.override.end),
        });
      }
      onJobCardPatch?.(next);
    } catch (err) {
      console.warn('Failed to check Outlook for job card date changes', err);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      await loadInstallEvents({ syncOutlook: true });
      const settled = await Promise.allSettled([
        documentsService.getDocumentsByProject(project.id),
        listJobAssignees(jobCard.id),
        listJobCardOrderAcceptanceFiles(project.id),
        listJobComments(jobCard.id),
        listJobCardImages(jobCard.id, 'survey_notes'),
        listJobCompletionEvents(jobCard.id),
        allowAssign ? listAssignableCrew() : Promise.resolve([] as AssignableProfile[]),
      ]);
      const value = <T,>(result: PromiseSettledResult<T>, fallback: T): T => (
        result.status === 'fulfilled' ? result.value : fallback
      );
      const firstError = settled.find((result) => result.status === 'rejected') as PromiseRejectedResult | undefined;
      setDocs(value(settled[0] as PromiseSettledResult<Document[]>, []));
      setAssignees(value(settled[1] as PromiseSettledResult<JobAssignee[]>, []));
      setOaFiles(value(settled[2] as PromiseSettledResult<JobCardOrderAcceptanceFile[]>, []));
      setComments(value(settled[3] as PromiseSettledResult<JobComment[]>, []));
      setSurveyImages(value(settled[4] as PromiseSettledResult<JobCardImage[]>, []));
      setCompletionEvents(value(settled[5] as PromiseSettledResult<JobCompletionEvent[]>, []));
      setCrew(value(settled[6] as PromiseSettledResult<AssignableProfile[]>, []));
      if (firstError) {
        setError(firstError.reason instanceof Error ? firstError.reason.message : 'Failed to load job card');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load job card');
    } finally {
      setLoading(false);
    }
  };

  const surveyNotes = surveyNotesFrom(
    { id: jobCard.id, survey_notes: jobCard.survey_notes },
    { metadata: project.metadata },
    isOldest,
  );

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobCard.id, project.id, allowAssign, showSpecLinks]);

  useEffect(() => {
    setSurveyDraft(surveyNotes);
  }, [surveyNotes]);

  useEffect(() => {
    const reload = () => { void loadInstallEvents().catch(() => undefined); };
    const channel = directSupabase
      .channel(`job-card-install-${project.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_events',
          filter: `project_id=eq.${project.id}`,
        },
        reload
      )
      .subscribe();
    window.addEventListener('focus', reload);
    return () => {
      window.removeEventListener('focus', reload);
      void directSupabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const install = latestTypedEvent(events, 'installationDateConfirmation');
  const delivery = latestTypedEvent(events, 'deliveryDateConfirmation');
  const calendarSpan = jobCardInstallSpan({ calendar: install || null });
  const scheduledInstallLabel = formatInstallSpan(calendarSpan) || formatEventStamp(install?.event_date) || 'Not scheduled';
  const installOverride = installDateOverrideFrom(jobCard, project);
  const installEndOverride = installEndOverrideFrom(jobCard, project);
  const overrideOn = !!installOverride || !!installEndOverride;
  const cardSpan = jobCardInstallSpan({
    calendar: install || null,
    overrideStart: installOverride,
    overrideEnd: installEndOverride,
    followsBooking: jobCard.follows_booking,
  });
  const installOverrideDate = toInstallDateOnly(cardSpan.start);
  const installOverrideTime = toInstallTimeOnly(cardSpan.start) || '';
  const installOverrideEndDate = cardSpan.days[cardSpan.days.length - 1] || installOverrideDate;
  const installOverrideEndTime = cardSpan.endTime || '';
  const installOverrideEnd = withInstallTime(installOverrideEndDate, installOverrideEndTime);
  const installDateLabel = overrideOn
    ? (formatInstallSpan(cardSpan) || scheduledInstallLabel)
    : jobCard.follows_booking ? scheduledInstallLabel : 'Not set: turn on Override to give this card its dates';
  const deliveryDateLabel = formatEventStamp(delivery?.event_date) || 'Not scheduled';
  const sensitiveCustomer = sensitiveCustomerFrom(project);
  const savedSurveyNotes = surveyNotesFrom(jobCard, project, isOldest);
  const surveyDirty = surveyDraft !== savedSurveyNotes;
  const chosen = docs.find((doc) => doc.kind === 'Estimate' || doc.kind === 'Quotation');
  const invoices = docs.filter((doc) => doc.kind === 'Invoice');
  const assigneeIds = useMemo(() => assignees.map((row) => row.profile_id), [assignees]);
  const completion = currentJobCompletion(completionEvents);
  const closure = currentJobCardClosure(completionEvents);
  const ended = closure?.action === 'ended' ? closure : null;
  const cardTitle = (id: string | null) => laterCards.find((card) => card.id === id)?.title || 'a later card';
  const canAttest = !closure && !!user?.id && !isLabourer && !isSupplier;
  const canCrudOwnComments = !isFitter && !isLabourer && !isSupplier;
  const canEditJobDetails = canCrudOwnComments && !!onProjectPatch;
  const canEditSurveyNotes = canCrudOwnComments;
  const canAttachImages = !isSupplier && profile?.role !== 'document';
  const canRelease = canCrudOwnComments && !!closure && !!user?.id;
  const canEnd = canCrudOwnComments && allowAssign && !closure && !!user?.id;

  const canManageComment = (comment: JobComment) => (
    canCrudOwnComments && !!user?.id && comment.author_id === user.id
  );
  const canDeleteImage = (image: JobCardImage) => (
    canCrudOwnComments || (!!user?.id && image.uploaded_by === user.id)
  );

  const reloadComments = async () => {
    setComments(await listJobComments(jobCard.id));
  };

  useEffect(() => {
    if (!allowAssign) return undefined;
    let cancelled = false;
    Promise.all([listSubcontractorAccounts(), listJobSubcontractorIds(jobCard.id)])
      .then(([accounts, ids]) => {
        if (cancelled) return;
        setSubAccounts(accounts);
        setSubIds(ids);
      })
      .catch(() => {
        if (!cancelled) setSubAccounts(null);
      });
    return () => { cancelled = true; };
  }, [allowAssign, jobCard.id]);

  const subbieEmails = useMemo(
    () => new Set((subAccounts || []).map((account) => account.keystone_email).filter(Boolean) as string[]),
    [subAccounts],
  );
  const isSubbie = (person: AssignableProfile | JobAssignee) => (
    !!person.email && subbieEmails.has(person.email.toLowerCase())
  );
  // A subbie with a Keystone email is assigned through their login, but only once that login is in
  // the crew list; until then (no account yet, or not a fitter/labourer) they stay assignable here.
  const crewEmails = useMemo(
    () => new Set(crew.map((person) => String(person.email || '').toLowerCase()).filter(Boolean)),
    [crew],
  );
  const offAppSubs = (subAccounts || []).filter((account) => (
    !account.keystone_email || !crewEmails.has(account.keystone_email) || subIds.includes(account.id)
  ));

  const handleSubsChange = async (nextIds: string[]) => {
    if (!user?.id || savingSubs) return;
    setSavingSubs(true);
    setError(null);
    try {
      await setJobSubcontractors(jobCard.id, project.id, nextIds, user.id);
      setSubIds(await listJobSubcontractorIds(jobCard.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subcontractors');
    } finally {
      setSavingSubs(false);
    }
  };

  const handleAssigneesChange = async (nextIds: string[]) => {
    if (!user?.id || savingAssignees) return;
    setSavingAssignees(true);
    setError(null);
    try {
      await setJobAssignees(jobCard.id, project.id, nextIds, user.id);
      setAssignees(await listJobAssignees(jobCard.id));
      onAssigneesChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update crew');
    } finally {
      setSavingAssignees(false);
    }
  };

  const handleComment = async () => {
    if (!user?.id || savingComment) return;
    if (!commentDraft.trim() && !commentPhotos.pending.length) return;
    setSavingComment(true);
    setError(null);
    try {
      const commentId = await addJobComment(jobCard.id, project.id, user.id, commentDraft);
      if (commentId && commentPhotos.pending.length) {
        let count = 0;
        for (const item of commentPhotos.pending) {
          await uploadJobCardImage({
            jobCardId: jobCard.id,
            projectId: project.id,
            authorId: user.id,
            kind: 'comment',
            commentId,
            file: item.file,
            existingCount: count,
          });
          count += 1;
        }
      }
      setCommentDraft('');
      commentPhotos.clear();
      await reloadComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add comment');
    } finally {
      setSavingComment(false);
    }
  };

  const handleSurveyPhotos = async (files: File[]) => {
    if (!user?.id || !files.length || uploadingSurvey) return;
    setUploadingSurvey(true);
    setError(null);
    try {
      let count = surveyImages.length;
      for (const file of files) {
        await uploadJobCardImage({
          jobCardId: jobCard.id,
          projectId: project.id,
          authorId: user.id,
          kind: 'survey_notes',
          file,
          existingCount: count,
        });
        count += 1;
      }
      setSurveyImages(await listJobCardImages(jobCard.id, 'survey_notes'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add media');
    } finally {
      setUploadingSurvey(false);
    }
  };

  const handleCommentPhotos = async (comment: JobComment, files: File[]) => {
    if (!user?.id || !files.length) return;
    setError(null);
    try {
      let count = comment.images.length;
      for (const file of files) {
        await uploadJobCardImage({
          jobCardId: jobCard.id,
          projectId: project.id,
          authorId: user.id,
          kind: 'comment',
          commentId: comment.id,
          file,
          existingCount: count,
        });
        count += 1;
      }
      await reloadComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add media');
    }
  };

  const handleDeleteImage = async (image: JobCardImage) => {
    if (deletingImageId) return;
    setDeletingImageId(image.id);
    setError(null);
    try {
      await deleteJobCardImage(image);
      if (image.kind === 'survey_notes') {
        setSurveyImages(await listJobCardImages(jobCard.id, 'survey_notes'));
      } else {
        await reloadComments();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete media');
    } finally {
      setDeletingImageId(null);
    }
  };

  const startEditComment = (comment: JobComment) => {
    setEditingCommentId(comment.id);
    setEditDraft(comment.body);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditDraft('');
  };

  const saveEditComment = async () => {
    if (!editingCommentId || savingEdit) return;
    setSavingEdit(true);
    setError(null);
    try {
      await updateJobComment(editingCommentId, editDraft);
      setEditingCommentId(null);
      setEditDraft('');
      await reloadComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update comment');
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDeleteComment = async () => {
    if (!deleteTarget || deletingCommentId) return;
    setDeletingCommentId(deleteTarget.id);
    setError(null);
    try {
      await deleteJobComment(deleteTarget.id);
      if (editingCommentId === deleteTarget.id) cancelEditComment();
      setDeleteTarget(null);
      await reloadComments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleOpenFile = async (file: JobCardOrderAcceptanceFile) => {
    setError(null);
    try {
      await openOaFile(file.id, () => openJobCardOrderAcceptance(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open file');
    }
  };

  const confirmComplete = async () => {
    if (!user?.id || !jobDone || !csfIssued) return;
    setAttesting(true);
    setError(null);
    try {
      await recordJobCompletion(jobCard.id, project.id, user.id);
      if (onAttestedComplete) await onAttestedComplete();
      setCompletionEvents(await listJobCompletionEvents(jobCard.id));
      setAttestOpen(false);
      setJobDone(false);
      setCsfIssued(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark job complete');
    } finally {
      setAttesting(false);
    }
  };

  const confirmRelease = async () => {
    if (!user?.id || !closure) return;
    setReleasing(true);
    setError(null);
    try {
      await recordJobRelease(jobCard.id, project.id, user.id);
      if (onReleased) await onReleased();
      setCompletionEvents(await listJobCompletionEvents(jobCard.id));
      setReleaseOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to release job');
    } finally {
      setReleasing(false);
    }
  };

  const openEndDialog = () => {
    setEndReason(END_REASONS[0]);
    setEndContinuedOn(laterCards.length === 1 ? laterCards[0].id : null);
    setEndOpen(true);
  };

  const confirmEnd = async () => {
    if (!user?.id || closure || !endContinuedOn) return;
    setEnding(true);
    setError(null);
    try {
      await recordJobEnded({
        jobCardId: jobCard.id,
        projectId: project.id,
        endedBy: user.id,
        reason: endReason,
        continuedOnCardId: endContinuedOn,
      });
      setCompletionEvents(await listJobCompletionEvents(jobCard.id));
      setEndOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to end job card');
    } finally {
      setEnding(false);
    }
  };

  const confirmDeleteCard = async () => {
    if (!onDeleteCard || deletingCard) return;
    setDeletingCard(true);
    setError(null);
    try {
      if (!isOldest && jobCard.calendar_event_id) await deleteCardDiaryEvent(jobCard.calendar_event_id);
      await onDeleteCard();
      setDeleteCardOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete job card');
    } finally {
      setDeletingCard(false);
    }
  };

  const patchJobMetadata = async (next: Record<string, unknown>) => {
    if (!onProjectPatch) return;
    await onProjectPatch({
      metadata: {
        ...asMetadata(project),
        ...next,
      },
    });
  };

  useEffect(() => {
    if (!canEditSurveyNotes) return;
    const fromRow = String(jobCard.survey_notes || '').trim();
    const fromProject = String(asMetadata(project).survey_notes || '').trim();
    if (fromRow === SURVEY_NOTES_PLACEHOLDER) {
      void updateJobCard(jobCard.id, { survey_notes: '' }).then((next) => {
        onJobCardPatch?.(next);
      });
    }
    if (fromProject === SURVEY_NOTES_PLACEHOLDER && onProjectPatch) {
      void onProjectPatch({
        metadata: {
          ...asMetadata(project),
          survey_notes: '',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobCard.id, canEditSurveyNotes]);

  const handleSensitiveChange = async (value: string | null) => {
    if (!canEditJobDetails || savingSensitive) return;
    const next = value === 'Y';
    if (next === sensitiveCustomer) return;
    setSavingSensitive(true);
    setError(null);
    try {
      await patchJobMetadata({ sensitive_customer: next });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update sensitive customer');
    } finally {
      setSavingSensitive(false);
    }
  };

  const handleInstallOverrideChange = async (nextOverride: string | null, nextEnd: string | null) => {
    if (!canEditSurveyNotes || savingInstallOverride) return;
    const normalised = nextOverride
      ? withInstallTime(toInstallDateOnly(nextOverride), toInstallTimeOnly(nextOverride))
      : null;
    const startDay = toInstallDateOnly(normalised);
    const startTime = toInstallTimeOnly(normalised);
    const requestedEnd = startDay ? toInstallDateOnly(nextEnd) : null;
    const endDay = startDay && requestedEnd && requestedEnd < startDay ? startDay : requestedEnd;
    // An end time needs a start time, and on a single day it must come after it
    const requestedEndTime = startTime && endDay ? toInstallTimeOnly(nextEnd) : null;
    const endTime = requestedEndTime && (endDay! > startDay! || requestedEndTime > startTime!) ? requestedEndTime : null;
    if (requestedEndTime && !endTime) {
      setError('End time must be after the start time.');
      return;
    }
    const endStamp = withInstallTime(endDay, endTime);
    if (normalised === installOverride && endStamp === installEndOverride) return;
    setSavingInstallOverride(true);
    setError(null);
    try {
      let next = await updateJobCard(jobCard.id, {
        install_date_override: startDay,
        install_end_date_override: endDay,
      });
      if (onProjectPatch) {
        await onProjectPatch({
          metadata: withPerCardInstallOverride(project, jobCard.id, normalised, endStamp),
        });
      }
      onJobCardPatch?.(next);
      // The override is the card's diary entry: keep Outlook in step
      try {
        if (isOldest) {
          if (normalised) {
            const eventType = project.supply_only ? 'deliveryDateConfirmation' : 'installationDateConfirmation';
            await moveBookingToOverride({
              project,
              booking: latestTypedEvent(events, eventType) || null,
              eventType,
              startStamp: normalised,
              endDay: endStamp,
              location: siteIdentity.siteAddress,
              customerName: siteIdentity.fullName || project.customer_name,
              userId: user?.id,
            });
            await loadInstallEvents();
          }
        } else {
          const eventId = await saveCardDiaryEvent({
            card: next,
            project,
            startStamp: normalised,
            endDay: endStamp,
            location: siteIdentity.siteAddress,
            customerName: siteIdentity.fullName || project.customer_name,
          });
          if (eventId !== next.calendar_event_id) {
            next = await updateJobCard(jobCard.id, { calendar_event_id: eventId });
            onJobCardPatch?.(next);
          }
        }
        setDiaryMissing(false);
      } catch (outlookErr) {
        const reason = outlookErr instanceof Error ? outlookErr.message : 'unknown error';
        setError(`Dates saved, but Outlook could not be updated: ${reason}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save install date override');
    } finally {
      setSavingInstallOverride(false);
    }
  };

  /**
   * Back to the backlog: no install (or delivery) date until one is agreed. Cancels the booking
   * (job card 1) or clears this card's dates, and deletes the Outlook invite if it is still there.
   */
  const moveToDatePending = async () => {
    if (!canEditSurveyNotes || movingToPending) return;
    setMovingToPending(true);
    setError(null);
    let outlookProblem: string | null = null;
    const removeInvite = async (eventId?: string | null) => {
      if (!eventId) return;
      try {
        await deleteCardDiaryEvent(eventId);
      } catch (err) {
        outlookProblem = err instanceof Error ? err.message : 'unknown error';
      }
    };
    try {
      if (isOldest) {
        const eventType = project.supply_only ? 'deliveryDateConfirmation' : 'installationDateConfirmation';
        const booking = latestTypedEvent(events, eventType);
        if (booking) {
          await removeInvite(booking.calendar_event_id);
          const { error: bookingError } = await directSupabase
            .from('project_events')
            .update({ status: 'cancelled' })
            .eq('id', booking.id);
          if (bookingError) throw new Error(bookingError.message);
        }
      } else {
        await removeInvite(jobCard.calendar_event_id);
      }
      const next = await updateJobCard(jobCard.id, {
        install_date_override: null,
        install_end_date_override: null,
        ...(isOldest ? {} : { calendar_event_id: null }),
      });
      if (onProjectPatch && installOverride) {
        await onProjectPatch({ metadata: withPerCardInstallOverride(project, jobCard.id, null) });
      }
      onJobCardPatch?.(next);
      await loadInstallEvents();
      setDiaryMissing(false);
      setPendingOpen(false);
      if (outlookProblem) {
        setError(`Moved to date pending, but the Outlook invite could not be deleted: ${outlookProblem}. Remove it in Outlook.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move to date pending');
    } finally {
      setMovingToPending(false);
    }
  };

  const handleSaveSurveyNotes = async () => {
    if (!canEditSurveyNotes || savingSurvey || !surveyDirty) return;
    setSavingSurvey(true);
    setError(null);
    try {
      const notes = persistedSurveyNotes(surveyDraft);
      const next = await updateJobCard(jobCard.id, { survey_notes: notes });
      if (onProjectPatch) {
        await onProjectPatch({
          metadata: withPerCardSurveyNotes(project, jobCard.id, notes),
        });
      }
      onJobCardPatch?.(next);
      setSurveyDraft(notes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save surveyor notes');
    } finally {
      setSavingSurvey(false);
    }
  };

  const handleCopyForWhatsApp = async () => {
    const text = jobCardShareText({
      title: jobCard.title || 'Job card',
      customerName: siteIdentity.fullName || project.customer_name,
      siteAddress: siteIdentity.siteAddress,
      phone: isSiteCrew ? null : siteIdentity.phone,
      dateLabel: project.supply_only ? 'Delivery' : 'Installation',
      dates: project.supply_only ? deliveryDateLabel : installDateLabel,
      sensitiveCustomer,
      surveyorNotes: savedSurveyNotes,
      spec: showSpecLinks && chosen ? `${chosen.lifecycleRef} · ${chosen.title}` : null,
      crew: assignees.map((person) => person.full_name || person.email || ''),
    });
    try {
      await navigator.clipboard.writeText(text);
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 2000);
    } catch {
      setError('Could not copy to the clipboard. Check the browser allows clipboard access.');
    }
  };

  return (
    <Stack gap={2} sx={{ height: '100%', minWidth: 0 }}>
      {loading && <CircularProgress size="sm" />}
      {error && <Alert color="danger">{error}</Alert>}

      <Card
        variant="outlined"
        sx={{
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'lg',
          bgcolor: 'background.surface',
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }} gap={1}>
            <Typography level="title-md">{jobCard.title || 'Job card'}</Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ ml: 'auto' }}>
            <Button
              size="sm"
              variant="plain"
              color={shareCopied ? 'success' : 'neutral'}
              startDecorator={<IconWrapper icon={FaWhatsapp} />}
              onClick={() => { void handleCopyForWhatsApp(); }}
              sx={{ fontWeight: 'md' }}
            >
              {shareCopied ? 'Copied' : 'Copy for WhatsApp'}
            </Button>
            {onDeleteCard && (
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                aria-label="Delete job card"
                disabled={deletingCard}
                onClick={() => setDeleteCardOpen(true)}
              >
                <IconWrapper icon={FaTrash} />
              </IconButton>
            )}
            </Stack>
          </Stack>
          <Stack gap={2}>
          <Box>
          <Stack gap={1.25}>
            <Typography level="body-sm"><b>Customer:</b> {siteIdentity.fullName || project.customer_name || '—'}</Typography>
            <Typography level="body-sm">
              <b>Site / Address:</b>{' '}
              {siteIdentity.siteAddress ? (
                <Box
                  component="a"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(siteIdentity.siteAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'primary.plainColor', textDecoration: 'none', wordBreak: 'break-word' }}
                >
                  {siteIdentity.siteAddress}
                </Box>
              ) : '—'}
            </Typography>
            {!isSiteCrew && (
              <Typography level="body-sm">
                <b>Phone:</b>{' '}
                {siteIdentity.phone ? (
                  <Box
                    component="a"
                    href={`tel:${siteIdentity.phone.replace(/\s+/g, '')}`}
                    sx={{ color: 'primary.plainColor', textDecoration: 'none' }}
                  >
                    {siteIdentity.phone}
                  </Box>
                ) : '—'}
              </Typography>
            )}
            <Stack gap={0.75}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                sx={{ flexWrap: 'wrap' }}
              >
                {canEditSurveyNotes && overrideOn ? (
                  <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 160 }}>
                      <FormLabel>Start Date</FormLabel>
                      <Input
                        type="date"
                        value={installOverrideDate || ''}
                        disabled={savingInstallOverride}
                        onChange={(event) => {
                          const next = withInstallTime(event.target.value, installOverrideTime);
                          if (next) void handleInstallOverrideChange(next, installOverrideEnd);
                        }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 120 }}>
                      <FormLabel>Time</FormLabel>
                      <Input
                        type="time"
                        slotProps={{ input: { step: QUARTER_HOUR_SECONDS } }}
                        value={installOverrideTime}
                        disabled={savingInstallOverride}
                        onChange={(event) => {
                          const next = withInstallTime(installOverrideDate, toQuarterHour(event.target.value));
                          if (next) void handleInstallOverrideChange(next, installOverrideEnd);
                        }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 160 }}>
                      <FormLabel>End Date</FormLabel>
                      <Input
                        type="date"
                        value={installOverrideEndDate || ''}
                        disabled={savingInstallOverride}
                        slotProps={{ input: { min: installOverrideDate || undefined } }}
                        onChange={(event) => {
                          const start = withInstallTime(installOverrideDate, installOverrideTime);
                          if (start && event.target.value) {
                            void handleInstallOverrideChange(start, withInstallTime(event.target.value, installOverrideEndTime));
                          }
                        }}
                      />
                    </FormControl>
                    <FormControl sx={{ minWidth: 120 }}>
                      <FormLabel>End Time</FormLabel>
                      <Input
                        type="time"
                        slotProps={{ input: { step: QUARTER_HOUR_SECONDS } }}
                        value={installOverrideEndTime}
                        disabled={savingInstallOverride || !installOverrideTime}
                        title={installOverrideTime ? 'Leave blank for an all-day diary entry' : 'Set a start time first'}
                        onChange={(event) => {
                          const start = withInstallTime(installOverrideDate, installOverrideTime);
                          if (start) void handleInstallOverrideChange(start, withInstallTime(installOverrideEndDate, toQuarterHour(event.target.value)));
                        }}
                      />
                    </FormControl>
                  </Stack>
                ) : (
                  <Typography level="body-sm">
                    <b>Installation Date:</b> {installDateLabel}
                    {overrideOn ? (
                      <Chip size="sm" variant="soft" color="warning" sx={{ ml: 0.75, verticalAlign: 'middle' }}>
                        Override
                      </Chip>
                    ) : null}
                  </Typography>
                )}
                {canEditSurveyNotes && (
                  <FormControl orientation="horizontal" sx={{ gap: 1 }}>
                    <FormLabel sx={{ m: 0 }}>Override</FormLabel>
                    <Switch
                      size="sm"
                      checked={overrideOn}
                      disabled={savingInstallOverride}
                      onChange={(event) => {
                        if (event.target.checked) {
                          const fallback = withInstallTime(
                            toInstallDateOnly(install?.event_date) || todayInstallDateOnly(),
                            toInstallTimeOnly(install?.event_date),
                          );
                          void handleInstallOverrideChange(
                            fallback,
                            calendarSpan.days[calendarSpan.days.length - 1] || null,
                          );
                          return;
                        }
                        void handleInstallOverrideChange(null, null);
                      }}
                    />
                  </FormControl>
                )}
              </Stack>
              {diaryMissing && (
                <Alert color="warning" size="sm">
                  {isOldest
                    ? 'The Outlook event for this booking has been deleted. Set new dates to rebook it, or move it to date pending until a new date is agreed.'
                    : 'The Outlook event for this job card has been deleted. Set new dates to rebook it, move it to date pending, or delete this job card.'}
                </Alert>
              )}
              {canEditSurveyNotes && allowAssign && !closure && cardSpan.days.length > 0 && (
                <Button
                  size="sm"
                  variant={diaryMissing ? 'solid' : 'plain'}
                  color={diaryMissing ? 'warning' : 'neutral'}
                  startDecorator={<IconWrapper icon={MdEventBusy} />}
                  onClick={() => setPendingOpen(true)}
                  sx={{ alignSelf: 'flex-start', fontWeight: 'md', ...(diaryMissing ? {} : { color: 'neutral.600', px: 0.75, minHeight: 28 }) }}
                >
                  Move to date pending
                </Button>
              )}
              {overrideOn && (
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                  Calendar invite: {scheduledInstallLabel}
                  {canEditSurveyNotes && calendarSpan.days.length > 1
                    ? '. To swap crews mid-job, end this card on the last day for its crew and raise another card for the remaining days.'
                    : ''}
                </Typography>
              )}
            </Stack>
            {project.supply_only && (
              <Typography level="body-sm">
                <b>Delivery Date:</b> {deliveryDateLabel}
              </Typography>
            )}
            {canEditJobDetails ? (
              <FormControl sx={{ maxWidth: { xs: '100%', sm: 220 } }}>
                <FormLabel>Sensitive Customer</FormLabel>
                <Select
                  value={sensitiveCustomer ? 'Y' : 'N'}
                  disabled={savingSensitive}
                  onChange={(_, value) => {
                    void handleSensitiveChange(typeof value === 'string' ? value : null);
                  }}
                >
                  <Option value="Y">Y</Option>
                  <Option value="N">N</Option>
                </Select>
              </FormControl>
            ) : (
              <Typography level="body-sm">
                <b>Sensitive Customer:</b> {sensitiveCustomer ? 'Y' : 'N'}
              </Typography>
            )}
            {canEditSurveyNotes ? (
              <FormControl>
                <FormLabel>Surveyor Notes</FormLabel>
                <Textarea
                  minRows={6}
                  value={surveyDraft}
                  onChange={(event) => setSurveyDraft(event.target.value)}
                  placeholder={SURVEY_NOTES_PLACEHOLDER}
                />
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Button
                    size="sm"
                    disabled={!surveyDirty || savingSurvey}
                    loading={savingSurvey}
                    onClick={() => { void handleSaveSurveyNotes(); }}
                  >
                    Save surveyor notes
                  </Button>
                  {canAttachImages && (
                    <JobCardAddPhotosButton
                      loading={uploadingSurvey}
                      onFiles={(files) => { void handleSurveyPhotos(files); }}
                    />
                  )}
                </Stack>
                <JobCardImageGallery
                  images={surveyImages}
                  canDelete={canDeleteImage}
                  deletingId={deletingImageId}
                  onDelete={(image) => { void handleDeleteImage(image); }}
                />
              </FormControl>
            ) : (
              <Box>
                <Typography level="body-sm" sx={{ mb: 0.5 }}><b>Surveyor Notes:</b></Typography>
                <Typography
                  level="body-sm"
                  sx={{ whiteSpace: 'pre-wrap', color: savedSurveyNotes ? 'text.primary' : 'neutral.500' }}
                >
                  {savedSurveyNotes || 'None'}
                </Typography>
                {canAttachImages && (
                  <Box sx={{ mt: 1 }}>
                    <JobCardAddPhotosButton
                      loading={uploadingSurvey}
                      onFiles={(files) => { void handleSurveyPhotos(files); }}
                    />
                  </Box>
                )}
                <JobCardImageGallery
                  images={surveyImages}
                  canDelete={canDeleteImage}
                  deletingId={deletingImageId}
                  onDelete={(image) => { void handleDeleteImage(image); }}
                />
              </Box>
            )}
            {showSpecLinks && (
              <>
                <Typography level="body-sm">
                  <b>Spec:</b> {chosen ? `${chosen.lifecycleRef} · ${chosen.title}` : 'No chosen quote/estimate'}
                </Typography>
                <Typography level="body-sm">
                  <b>Invoice:</b>{' '}
                  {invoices.length === 0
                    ? 'None linked'
                    : invoices.map((doc) => `${documentDisplayRef(doc)} (${doc.status})`).join(', ')}
                </Typography>
              </>
            )}
          </Stack>
          </Box>

          <JobCardSection
            title="Assigned crew"
          >
          {allowAssign ? (
            <Stack gap={1.5}>
            <FormControl>
              <FormLabel>Fitters, labourers and admins</FormLabel>
              <Select
                multiple
                placeholder="Assign crew..."
                value={assigneeIds}
                disabled={savingAssignees || crew.length === 0}
                onChange={(_, value) => {
                  const ids = (Array.isArray(value) ? value : []).map(String);
                  void handleAssigneesChange(ids);
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {(Array.isArray(selected) ? selected : []).map((item) => {
                      const id = typeof item === 'string' ? item : String((item as { value?: string }).value || '');
                      const person = crew.find((row) => row.id === id)
                        || assignees.find((row) => row.profile_id === id);
                      return (
                        <Chip key={id} size="sm" variant="soft">
                          {person ? crewLabel(person, isSubbie(person)) : id}
                        </Chip>
                      );
                    })}
                  </Box>
                )}
                slotProps={{ listbox: { sx: { maxHeight: 260, overflow: 'auto' } } }}
              >
                {crew.map((person) => (
                  <Option key={person.id} value={person.id}>{crewLabel(person, isSubbie(person))}</Option>
                ))}
              </Select>
            </FormControl>
            {subAccounts && (
              <FormControl>
                <FormLabel>Subcontractors without a login</FormLabel>
                <Select
                  multiple
                  placeholder={offAppSubs.length ? 'Add a subcontractor...' : 'No subcontractors in the directory'}
                  value={subIds}
                  disabled={savingSubs || offAppSubs.length === 0}
                  onChange={(_, value) => {
                    const ids = (Array.isArray(value) ? value : []).map(String);
                    void handleSubsChange(ids);
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(Array.isArray(selected) ? selected : []).map((item) => {
                        const id = typeof item === 'string' ? item : String((item as { value?: string }).value || '');
                        const account = offAppSubs.find((row) => row.id === id);
                        return (
                          <Chip key={id} size="sm" variant="soft">
                            {account ? `${account.name} (Subcontractor)` : id}
                          </Chip>
                        );
                      })}
                    </Box>
                  )}
                  slotProps={{ listbox: { sx: { maxHeight: 260, overflow: 'auto' } } }}
                >
                  {offAppSubs.map((account) => (
                    <Option key={account.id} value={account.id}>{account.name}</Option>
                  ))}
                </Select>
                <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 0.5 }}>
                  From the directory. Recorded for costing and CIS only; they don&apos;t see this card.
                  Not listed? Tick &quot;Keystone subcontractor&quot; or set a CIS band on their directory entry.
                </Typography>
              </FormControl>
            )}
            </Stack>
          ) : (
            <Stack gap={0.5}>
              {assignees.length === 0 && (
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No crew assigned yet.</Typography>
              )}
              {assignees.map((person) => (
                <Typography key={person.profile_id} level="body-sm">{crewLabel(person)}</Typography>
              ))}
            </Stack>
          )}
          </JobCardSection>

          <JobCardSection title="OA (Order Acceptance)">
          {oaFiles.length === 0 ? (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              No price-redacted Order Acceptance is ready yet.
            </Typography>
          ) : (
            <Stack gap={0.75}>
              {oaFiles.map((file) => (
                <Stack
                  key={file.id}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  gap={1}
                >
                  <Typography level="body-sm" sx={{ wordBreak: 'break-word' }}>{file.name}</Typography>
                  <Button
                    size="md"
                    variant="outlined"
                    loading={openingFile === file.id}
                    onClick={() => { void handleOpenFile(file); }}
                    sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}
                  >
                    Open
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
          </JobCardSection>

          <JobCardSection
            title="Comments"
            hint={!isOldest && continuedFrom ? `Continued from ${continuedFrom}: earlier comments are on that card.` : undefined}
          >
          <Stack gap={1.25}>
            {comments.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>No comments yet.</Typography>
            )}
            {comments.map((comment) => {
              const managing = canManageComment(comment);
              const editing = editingCommentId === comment.id;
              return (
                <Box key={comment.id}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      <Box component="span" sx={{ fontWeight: 800, color: 'text.primary', fontSize: '0.9rem' }}>
                        {comment.author_name || 'Crew'}
                      </Box>
                      {' · '}
                      {formatStamp(comment.created_at)}
                    </Typography>
                    {managing && !editing && (
                      <Stack direction="row" gap={0.25}>
                        <IconButton
                          size="sm"
                          variant="plain"
                          aria-label="Edit comment"
                          onClick={() => startEditComment(comment)}
                        >
                          <IconWrapper icon={FaEdit} />
                        </IconButton>
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          aria-label="Delete comment"
                          onClick={() => setDeleteTarget(comment)}
                        >
                          <IconWrapper icon={FaTrash} />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                  {editing ? (
                    <Stack gap={0.75} sx={{ mt: 0.5 }}>
                      <Textarea
                        minRows={2}
                        value={editDraft}
                        onChange={(event) => setEditDraft(event.target.value)}
                      />
                      <JobCardImageGallery
                        images={comment.images}
                        canDelete={canDeleteImage}
                        deletingId={deletingImageId}
                        onDelete={(image) => { void handleDeleteImage(image); }}
                      />
                      <Stack direction="row" justifyContent="flex-end" gap={1} flexWrap="wrap">
                        <JobCardAddPhotosButton
                          label="Add media"
                          onFiles={(files) => { void handleCommentPhotos(comment, files); }}
                        />
                        <Button size="sm" variant="plain" disabled={savingEdit} onClick={cancelEditComment}>
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          loading={savingEdit}
                          onClick={() => { void saveEditComment(); }}
                        >
                          Save
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <>
                      {comment.body ? (
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{comment.body}</Typography>
                      ) : null}
                      <JobCardImageGallery
                        images={comment.images}
                        canDelete={canDeleteImage}
                        deletingId={deletingImageId}
                        onDelete={(image) => { void handleDeleteImage(image); }}
                      />
                    </>
                  )}
                </Box>
              );
            })}
            <Textarea
              minRows={2}
              placeholder="Add a comment for this job..."
              value={commentDraft}
              onChange={(event) => setCommentDraft(event.target.value)}
            />
            <JobCardPendingThumbs files={commentPhotos.pending} onRemove={commentPhotos.remove} />
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="flex-end" gap={1}>
              {canAttachImages && (
                <JobCardAddPhotosButton
                  disabled={savingComment}
                  onFiles={commentPhotos.addFiles}
                />
              )}
              <Button
                size="md"
                disabled={!commentDraft.trim() && !commentPhotos.pending.length}
                loading={savingComment}
                onClick={() => { void handleComment(); }}
                sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}
              >
                Add comment
              </Button>
            </Stack>
          </Stack>
          </JobCardSection>

          <JobCardSection title="Completion">
          <Stack gap={1.25}>
            {isLabourer && !completion && (
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                Labourers can view this job and add comments, but cannot mark it complete.
              </Typography>
            )}
            {completion ? (
              <Stack gap={0.5}>
                <Typography level="body-sm">
                  Attested by {completion.actor_name || profile?.full_name || 'crew'} on {formatStamp(completion.attested_at)}.
                </Typography>
                <Typography level="body-sm">Job is complete.</Typography>
                <Typography level="body-sm">Customer Satisfaction Form issued.</Typography>
              </Stack>
            ) : ended ? (
              <Stack gap={0.5}>
                <Typography level="body-sm">
                  Ended by {ended.actor_name || 'office'} on {formatStamp(ended.attested_at)}.
                </Typography>
                <Typography level="body-sm">
                  Continued on {cardTitle(ended.continued_on_card_id)}{ended.end_reason ? ` · ${ended.end_reason}` : ''}.
                </Typography>
              </Stack>
            ) : !isLabourer ? (
              <Button disabled={!canAttest} onClick={() => setAttestOpen(true)} sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}>
                Mark job as complete
              </Button>
            ) : null}
            {canEnd && (
              <Button variant="outlined" onClick={openEndDialog} sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}>
                End card – continued on a later card
              </Button>
            )}
            {canRelease && (
              <Stack direction="row" justifyContent="flex-start">
                <Button onClick={() => setReleaseOpen(true)} sx={{ width: { xs: '100%', sm: 'auto' }, minHeight: 44 }}>
                  {ended ? 'Reopen card' : 'Release job'}
                </Button>
              </Stack>
            )}
            {completionEvents.length > 0 && (
              <Stack gap={0.5}>
                <Typography level="body-xs" sx={{ color: 'neutral.500' }}>Audit trail</Typography>
                {completionEvents.map((event) => (
                  <Typography key={event.id} level="body-xs" sx={{ color: 'neutral.500' }}>
                    {formatStamp(event.attested_at)} · {event.actor_name || 'Crew'} ·{' '}
                    {event.action === 'released' ? 'Released' : event.action === 'ended' ? 'Ended' : 'Completed'}
                    {event.action === 'completed' ? ' (job complete, CSF issued)' : ''}
                    {event.action === 'ended' ? ` (continued on ${cardTitle(event.continued_on_card_id)})` : ''}
                  </Typography>
                ))}
              </Stack>
            )}
          </Stack>
          </JobCardSection>
          </Stack>
        </CardContent>
      </Card>

      <JobCardOaPreviewModal preview={oaPreview} onClose={closeOaPreview} />

      <Modal open={releaseOpen} onClose={() => !releasing && setReleaseOpen(false)}>
        <ModalDialog>
          <ModalClose disabled={releasing} />
          <DialogTitle>{ended ? 'Reopen ended card' : 'Release completed job'}</DialogTitle>
          <DialogContent>
            <Stack gap={1.5}>
              <Typography level="body-sm">
                {ended
                  ? 'This reopens the card so its crew can work it again. Comments stay, and the original end remains on the audit trail.'
                  : 'This reopens the job so it can be worked again. Comments stay, and the original completion remains on the audit trail.'}
              </Typography>
              <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Button variant="plain" disabled={releasing} onClick={() => setReleaseOpen(false)}>Cancel</Button>
                <Button
                  loading={releasing}
                  onClick={() => { void confirmRelease(); }}
                >
                  {ended ? 'Reopen' : 'Release'}
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>

      <Modal open={pendingOpen} onClose={() => !movingToPending && setPendingOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480 }}>
          <ModalClose disabled={movingToPending} />
          <DialogTitle>{project.supply_only ? 'Delivery date pending' : 'Installation date pending'}</DialogTitle>
          <DialogContent>
            <Stack gap={1.5}>
              <Typography level="body-sm">
                {isOldest
                  ? 'This cancels the booking, clears this card\'s dates and deletes the Outlook invite if it is still there.'
                  : 'This clears this card\'s dates and deletes its Outlook invite if it is still there.'}
                {' '}The job moves to Date pending on the Job Cards page until you set new dates.
              </Typography>
              <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Button variant="plain" disabled={movingToPending} onClick={() => setPendingOpen(false)}>Cancel</Button>
                <Button loading={movingToPending} onClick={() => { void moveToDatePending(); }}>Move to date pending</Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>

      <Modal open={endOpen} onClose={() => !ending && setEndOpen(false)}>
        <ModalDialog sx={{ maxWidth: 480 }}>
          <ModalClose disabled={ending} />
          <DialogTitle>End card – continued on a later card</DialogTitle>
          <DialogContent>
            <Stack gap={1.5}>
              <Typography level="body-sm">
                Use this when the job stops part-way, for example a delay or a crew change. This card keeps the days
                its crew worked, its crew no longer see it, and no completion steps run. The job is marked complete
                on the last card.
              </Typography>
              <Typography level="body-sm">
                Before ending, check this card&apos;s dates cover only the days its crew actually worked.
              </Typography>
              {laterCards.length === 0 ? (
                <Alert color="warning" variant="soft">
                  Raise the card that continues this job first, then end this one.
                </Alert>
              ) : (
                <>
                  <FormControl>
                    <FormLabel>Continued on</FormLabel>
                    <Select
                      value={endContinuedOn}
                      placeholder="Choose a card"
                      onChange={(_, value) => setEndContinuedOn(value)}
                    >
                      {laterCards.map((card) => (
                        <Option key={card.id} value={card.id}>{card.title}</Option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Reason</FormLabel>
                    <Select value={endReason} onChange={(_, value) => value && setEndReason(value)}>
                      {END_REASONS.map((reason) => (
                        <Option key={reason} value={reason}>{reason}</Option>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Button variant="plain" disabled={ending} onClick={() => setEndOpen(false)}>Cancel</Button>
                <Button
                  disabled={!endContinuedOn}
                  loading={ending}
                  onClick={() => { void confirmEnd(); }}
                >
                  End card
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>

      <Modal open={attestOpen} onClose={() => !attesting && setAttestOpen(false)}>
        <ModalDialog>
          <ModalClose disabled={attesting} />
          <DialogTitle>Confirm job completion</DialogTitle>
          <DialogContent>
            <Stack gap={1.5}>
              <Typography level="body-sm">
                Both statements must be confirmed before this job can be marked complete.
              </Typography>
              <Checkbox
                checked={jobDone}
                label="I confirm the job is complete"
                onChange={(event) => setJobDone(event.target.checked)}
              />
              <Checkbox
                checked={csfIssued}
                label="I confirm I have issued the customer with the Customer Satisfaction Form"
                onChange={(event) => setCsfIssued(event.target.checked)}
              />
              <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Button variant="plain" disabled={attesting} onClick={() => setAttestOpen(false)}>Cancel</Button>
                <Button
                  disabled={!jobDone || !csfIssued}
                  loading={attesting}
                  onClick={() => { void confirmComplete(); }}
                >
                  Confirm
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>

      <Modal open={!!deleteTarget} onClose={() => !deletingCommentId && setDeleteTarget(null)}>
        <ModalDialog>
          <ModalClose disabled={!!deletingCommentId} />
          <DialogTitle>Delete comment</DialogTitle>
          <DialogContent>
            <Stack gap={1.5}>
              <Typography level="body-sm">This comment will be removed from the job card.</Typography>
              <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Button variant="plain" disabled={!!deletingCommentId} onClick={() => setDeleteTarget(null)}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  loading={!!deletingCommentId}
                  onClick={() => { void confirmDeleteComment(); }}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
      <Modal open={deleteCardOpen} onClose={() => !deletingCard && setDeleteCardOpen(false)}>
        <ModalDialog>
          <ModalClose disabled={deletingCard} />
          <DialogTitle>Delete job card</DialogTitle>
          <DialogContent>
            <Stack gap={1.5}>
              <Typography level="body-sm">
                This removes the card, its assignees, comments, and completion history. OA copies on the workspace are kept.
              </Typography>
              <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Button variant="plain" disabled={deletingCard} onClick={() => setDeleteCardOpen(false)}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  loading={deletingCard}
                  onClick={() => { void confirmDeleteCard(); }}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Stack>
  );
};

export default JobCard;
